import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../../firebase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const phoneRegex = /^\+?[0-9]{7,15}$/;

const NewConversationModal = ({ open, onClose, onConversationCreated }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPhoneNumber('');
      setMessage('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedPhone = phoneNumber.trim();
    if (!trimmedPhone) {
      setError('Phone number is required');
      return;
    }

    if (!phoneRegex.test(trimmedPhone)) {
      setError('Enter a valid phone number with digits and optional leading +');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('You must be logged in to start a conversation');
      return;
    }

    try {
      setSubmitting(true);
      const token = await currentUser.getIdToken(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/conversations/start`,
        {
          phoneNumber: trimmedPhone,
          message: message.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const conversationId = response?.data?.conversationId;
      const clientPhone = response?.data?.clientPhone;
      if (!conversationId) {
        setError('Unexpected response from server');
        return;
      }

      if (onConversationCreated) {
        onConversationCreated(conversationId, clientPhone);
      }

      onClose();
    } catch (err) {
      console.error('Failed to start conversation', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to start conversation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>
            Start a new SMS thread with a customer. You can optionally send the first message now.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700" htmlFor="phoneNumber">
              Phone Number
            </label>
            <Input
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+15551234567"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700" htmlFor="message">
              Initial Message (optional)
            </label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! This is Acme Roofing. How can we help today?"
              rows={3}
            />
          </div>
          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Starting...' : 'Start Conversation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationModal;
